import type { Alert } from "../../domain/alerts";
import type { InspectionDetail, Observation } from "../../domain/inspections";
import { anchorOf, type LonLat, type MapFeature } from "../../domain/geo";
import { isOpen, type WorkOrder } from "../../domain/workOrders";
import { tn } from "../../i18n";

/**
 * The operational layers: what is happening, placed on the drawing.
 *
 * Work orders and alerts carry their own point (the platform's
 * PointOnSurface of the asset's, else the zone's, geometry). An alert from an
 * older platform without one is placed on the feature carrying its asset tag
 * (the pack maps `attr_TAG` onto `props.tag`), else on the zone's pavement.
 * Inspection failures use the observation's position when the inspector
 * recorded one, else the same tag/zone rule. A marker that cannot be placed
 * is counted and listed, never invented.
 */

export type MarkerKind = "work_order" | "alert" | "finding";

export interface Marker {
  id: string;
  kind: MarkerKind;
  at: LonLat;
  title: string;
  subtitle: string;
  /** Severity 1–5 for alerts, priority 1–5 for work orders (1 = worst), severity for findings. */
  weight: number;
  href: string;
  when: string;
  ref: WorkOrder | Alert | { inspection: InspectionDetail; observation: Observation };
}

export interface Placement {
  markers: Marker[];
  unplaced: { kind: MarkerKind; title: string }[];
}

export function tagIndex(features: readonly MapFeature[]): Map<string, LonLat> {
  const m = new Map<string, LonLat>();
  for (const f of features) {
    const tag = f.props.tag;
    if (typeof tag === "string" && !m.has(tag)) m.set(tag, anchorOf(f.geometry));
  }
  return m;
}

function locate(tags: Map<string, LonLat>, assetTag?: string, zoneCode?: string): LonLat | undefined {
  if (assetTag && tags.has(assetTag)) return tags.get(assetTag);
  if (zoneCode && tags.has(zoneCode)) return tags.get(zoneCode);
  return undefined;
}

export function placeWorkOrders(orders: readonly WorkOrder[], tags: Map<string, LonLat>, since?: Date): Placement {
  const out: Placement = { markers: [], unplaced: [] };
  for (const w of orders) {
    if (!isOpen(w.status)) continue;
    if (since && new Date(w.openedAt) < since) continue;
    const at = w.point ? ([w.point.lon, w.point.lat] as LonLat) : locate(tags, w.assetTag, w.zoneCode);
    if (!at) {
      out.unplaced.push({ kind: "work_order", title: `${w.number} — ${w.title}` });
      continue;
    }
    out.markers.push({
      id: `wo:${w.id}`,
      kind: "work_order",
      at,
      title: `${w.number} · ${w.title}`,
      subtitle: `P${w.priority} · ${w.status.replaceAll("_", " ")}${w.crew ? ` · ${w.crew}` : ""}`,
      weight: w.priority,
      href: `/work-orders/${w.id}`,
      when: w.openedAt,
      ref: w,
    });
  }
  return out;
}

export function placeAlerts(alerts: readonly Alert[], tags: Map<string, LonLat>, since?: Date): Placement {
  const out: Placement = { markers: [], unplaced: [] };
  for (const a of alerts) {
    if (since && new Date(a.raisedAt) < since) continue;
    const at = a.point ? ([a.point.lon, a.point.lat] as LonLat) : locate(tags, a.assetTag, a.zoneCode);
    if (!at) {
      out.unplaced.push({ kind: "alert", title: a.title });
      continue;
    }
    out.markers.push({
      id: `al:${a.id}`,
      kind: "alert",
      at,
      title: a.title,
      subtitle: `S${a.severity} · ${a.kind}${a.ruleKey ? ` · ${a.ruleKey}` : ""}`,
      weight: 6 - a.severity,
      href: `/alerts?open=${a.id}`,
      when: a.raisedAt,
      ref: a,
    });
  }
  return out;
}

export function placeFindings(rounds: readonly InspectionDetail[], tags: Map<string, LonLat>, since?: Date): Placement {
  const out: Placement = { markers: [], unplaced: [] };
  for (const r of rounds) {
    const when = r.completedAt ?? r.startedAt ?? r.scheduledFor ?? "";
    if (since && when && new Date(when) < since) continue;
    for (const o of r.observations) {
      if (o.result !== "fail") continue;
      const at = o.point ? ([o.point.lon, o.point.lat] as LonLat) : locate(tags, o.assetTag, o.zoneCode);
      const label = `${o.itemKey.replaceAll("_", " ")}${o.assetTag ? ` · ${o.assetTag}` : o.zoneCode ? ` · ${o.zoneCode}` : ""}`;
      if (!at) {
        out.unplaced.push({ kind: "finding", title: `${r.templateName}: ${label}` });
        continue;
      }
      out.markers.push({
        id: `ob:${o.id}`,
        kind: "finding",
        at,
        title: label,
        subtitle: `${r.templateName} · ${tn(o.photos, "{n} photo", "{n} photos")}`,
        weight: 3,
        href: `/inspections/${r.id}`,
        when,
        ref: { inspection: r, observation: o },
      });
    }
  }
  return out;
}

/** Fixed, not theme tokens: the stage stays dark in both themes, so the light palette's darkened state colours would vanish on it. */
export const MARKER_COLOUR: Record<MarkerKind, string> = {
  work_order: "#f2c230",
  alert: "#e0524e",
  finding: "#4c9be8",
};

export const MARKER_LABEL: Record<MarkerKind, string> = {
  work_order: "Open work orders",
  alert: "Open alerts",
  finding: "Inspection failures",
};
